import { createQueryKeyStore } from "@lukemorales/query-key-factory";
import OpenApiClient from "../OpenApiClient";
import { getGameStoreUrl } from "./getGameStoreUrl";
/**
 * @description
 * Фабрика ключей для @tanstack/react-query.
 * Эта фабрика централизует управление ключами запросов в приложении,
 * обеспечивая строгую типизацию и автодополнение.
 *
 * Структура зеркально отражает OpenApiClient, что упрощает навигацию и использование.
 * Каждый ключ содержит не только сам queryKey, но и соответствующую queryFn для выполнения запроса.
 *
 * @example
 * // Использование в компоненте
 * import { useQuery } from '@tanstack/react-query';
 * import { queries } from '@app/Api/queries';
 *
 * const { data } = useQuery({
 *   ...queries.profile.get({ game: 'my-game' })
 * });
 *
 * const { data: friends } = useQuery({
 *   ...queries.friends.getFriends({ game: 'my-game', accountIds: [1, 2, 3] })
 * });
 */
export const queries = createQueryKeyStore({
    sdk: {
        initialize: (request) => ["fxid-sdk", "initialize", request]
    },
    auth: {
        /**
         * @description Получение информации о пользователе по Oauth токену.
         * @param game - Идентификатор игры.
         */
        oauthUserInfo: (game) => ({
            queryKey: [game],
            queryFn: () => OpenApiClient.Auth.getFxIdWebFeaturesAuthOauthUserInfoEndpoint(game)
        }),
        authenticateFromTempToken: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Auth.fxIdWebFeaturesAuthAuthenticateFromTokenEndpoint(request)
        }),
        authenticateFromFirebase: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Auth.fxIdWebFeaturesAuthAuthenticateFromFirebaseEndpoint(request)
        })
    },
    bank131: {
        /**
         * @description Получение данных для формы оплаты Bank131.
         * @param publicId - Публичный идентификатор заказа.
         */
        paymentFormData: (publicId) => ({
            queryKey: [publicId],
            queryFn: () => OpenApiClient.Bank131.fxIdWebFeaturesStoreBank131PaymentFormDataEndpoint(publicId)
        })
    },
    dataConnections: {
        /**
         * @description Получение данных из внешних источников (например, рейтингов).
         * @param command - Команда для получения данных.
         */
        get: (command) => ({
            queryKey: [command],
            queryFn: () => OpenApiClient.DataConnections.fxIdWebFeaturesDataConnectionsGetDataEndpoint(command)
        })
    },
    friends: {
        /**
         * @description Получение списка друзей.
         * @param request - Запрос с параметрами для получения друзей.
         */
        getFriends: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Friends.fxIdWebFeaturesFriendsGetFriendsEndpoint(request)
        })
    },
    ftlIntegration: {
        /**
         * @description Получение доступных веток для FTL.
         * @param game - Идентификатор игры.
         */
        availableBranches: (game) => ({
            queryKey: [game],
            queryFn: () => OpenApiClient.FtlIntegration.fxIdWebFeaturesFtlIntegrationLauncherAvailableBranchesEndpoint(game)
        }),
        /**
         * @description Получение манифеста лаунчера.
         * @param game - Идентификатор игры.
         * @param branch - Название ветки.
         */
        launcherManifest: (game, branch) => ({
            queryKey: [game, branch],
            queryFn: () => OpenApiClient.FtlIntegration.fxIdWebFeaturesFtlIntegrationLauncherLauncherManifestEndpoint(game, branch)
        })
    },
    games: {
        /**
         * @description Получение списка принятых пользователем документов.
         */
        getAcceptedDocuments: {
            queryKey: null,
            queryFn: () => OpenApiClient.Games.fxIdWebFeaturesDocumentsGetAcceptedDocumentsGetAcceptedDocumentsEndpoint()
        },
        /**
         * @description Получение всех документов с их версиями.
         * @param request - Запрос с параметрами для получения документов.
         */
        getAllDocumentsWithVersions: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Games.fxIdWebFeaturesDocumentsGetAllDocumentsWithVersionsGetAllDocumentsWithVersionsEndpoint(request)
        }),
        /**
         * @description Получение последних непринятых документов.
         * @param request - Запрос с параметрами для получения документов.
         */
        getUnacceptedDocuments: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Games.fxIdWebFeaturesDocumentsGetUnacceptedDocumentsGetUnacceptedDocumentsEndpoint(request)
        }),
        /**
         * @description Получение списка игр.
         */
        list: {
            queryKey: null,
            queryFn: () => OpenApiClient.Games.fxIdWebFeaturesGamesListEndpoint()
        },
        /**
         * @description Получение списка игр с их конфигурациями.
         */
        listWithConfigs: {
            queryKey: null,
            queryFn: () => OpenApiClient.Games.fxIdWebFeaturesGamesListWithConfigsEndpoint()
        }
    },
    info: {
        /**
         * @description Проверка существования аккаунта по социальному идентификатору.
         * @param game - Идентификатор игры.
         * @param socialIdentifierType - Тип социального идентификатора.
         * @param socialIdentifier - Значение социального идентификатора.
         */
        checkAccountExists: (game, socialIdentifierType, socialIdentifier) => ({
            queryKey: [game, socialIdentifierType, socialIdentifier],
            queryFn: () => OpenApiClient.Info.fxIdWebFeaturesInfoCheckAccountExistsBySocialIdentifierEndpoint(game, socialIdentifierType, socialIdentifier)
        }),
        /**
         * @description Получение новостей.
         * @param request - Запрос с параметрами для получения новостей.
         */
        news: (request) => ({
            queryKey: ["news", request.GameSystemName, request.Locale ?? "en"],
            queryFn: () => OpenApiClient.Info.fxIdWebFeaturesInfoNewsEndpoint(request),
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
        })
    },
    invite: {
        /**
         * @description Получение статуса приглашения по email.
         * @param request - Запрос с токеном приглашения.
         */
        emailInviteStatus: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Invite.fxIdWebFeaturesInviteEmailInviteStatusEndpoint(request)
        })
    },
    manifest: {
        /**
         * @description Получение манифеста для PWA.
         * @param params - Параметры для получения манифеста.
         */
        get: (params) => ({
            queryKey: [params.game, params.publicUrl, params.startUrl, params.token],
            queryFn: () => OpenApiClient.Manifest.fxIdWebFeaturesPlayManifestEndpoint(params.game)
        })
    },
    native: {
        /**
         * @description Получение статуса нативной сессии.
         * @param game - Идентификатор игры.
         * @param token - Токен сессии.
         */
        status: (game, token) => ({
            queryKey: [game, token],
            queryFn: () => OpenApiClient.Native.fxIdWebFeaturesNativeStatusEndpoint(game, token)
        })
    },
    play: {
        /**
         * @description Выбор игры для запуска.
         * @param request - Запрос с параметрами выбора игры.
         */
        chooseGame: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Play.fxIdWebFeaturesGamesChooseGameEndpoint(request)
        }),
        /**
         * @description Получение данных для запуска игры.
         * @param params - Параметры для запуска игры.
         */
        game: (params) => ({
            queryKey: [params.game, params.otaVersion, params.buildNumber, params.webPublishingPlatform],
            queryFn: () => OpenApiClient.Play.fxIdWebFeaturesPlayGameEndpoint(params.game, params.otaVersion, params.buildNumber, params.webPublishingPlatform)
        }),
        /**
         * @description Получение списка доступных версий игры.
         * @param game - Идентификатор игры.
         * @param otaVersion - Версия OTA.
         */
        list: (game, otaVersion) => ({
            queryKey: [game, otaVersion],
            queryFn: () => OpenApiClient.Play.fxIdWebFeaturesPlayListEndpoint(game, otaVersion)
        })
    },
    profile: {
        /**
         * @description Получение данных о связи игрового аккаунта с FxID.
         * @param request - Запрос с параметрами.
         */
        fetchFxIdGameConnection: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Profile.fxIdWebFeaturesProfileFetchFxIdGameConnectionEndpoint(request)
        }),
        /**
         * @description Получение профиля пользователя.
         * @param request - Запрос с параметрами для получения профиля.
         */
        get: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Profile.fxIdWebFeaturesProfileGetEndpoint(request)
        })
    },
    publicGameConfig: {
        /**
         * @description Получение публичной конфигурации игры.
         * @param game - Идентификатор игры.
         * @param webPublishingPlatform - Платформа паблишинга.
         */
        get: (game, webPublishingPlatform) => ({
            queryKey: [game, webPublishingPlatform],
            queryFn: () => OpenApiClient.PublicGameConfigApi.fxIdWebFeaturesPlayGamePublicConfigEndpoint(game, webPublishingPlatform)
        })
    },
    store: {
        /**
         * @description Получение статуса платежа Aghanim.
         * @param publicOrderId - Публичный идентификатор заказа.
         */
        aghanimPaymentStatus: (publicOrderId) => ({
            queryKey: [publicOrderId],
            queryFn: () => OpenApiClient.Store.fxIdWebFeaturesStoreAghanimPaymentStatusPaymentStatusEndpoint(publicOrderId)
        }),
        /**
         * @description Проверка возможности покупки продуктов.
         * @param request - Запрос с продуктами для проверки.
         */
        checkBuyProducts: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Store.fxIdWebFeaturesStoreCheckBuyProductsCheckBuyProductsEndpoint(request)
        }),
        /**
         * @description Получение статуса платежа.
         * @param request - Запрос с идентификатором заказа.
         */
        getPaymentStatus: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Store.fxIdWebFeaturesStoreGetPaymentStatusEndpoint(request)
        }),
        /**
         * @description Проверка доступности магазина.
         */
        availability: {
            queryKey: null,
            queryFn: () => OpenApiClient.Store.fxIdWebFeaturesStoreStoreAvailabilityEndpoint()
        },
        /**
         * @description Получение данных магазина.
         * @param request - Запрос с параметрами для получения данных.
         */
        data: (request) => ({
            queryKey: [request],
            queryFn: () => OpenApiClient.Store.fxIdWebFeaturesStoreStoreDataEndpoint(request)
        }),
        gameStoreUrl: (params) => ({
            queryKey: ["storeUrl", params],
            queryFn: async () => {
                // Вынесем логику в отдельную функцию для чистоты
                return getGameStoreUrl(params.game, params.redirectWithTempToken);
            }
        })
    }
});
//# sourceMappingURL=Queries.js.map